import React, { useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Badge, Alert, Collapse } from 'reactstrap';

export default function ImmiAccountGuide() {
  const [openStep, setOpenStep] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [activeSection, setActiveSection] = useState('before');

  const toggleStep = (stepId) => {
    setOpenStep(openStep === stepId ? null : stepId);
  };

  const markComplete = (stepId) => {
    if (completedSteps.includes(stepId)) {
      setCompletedSteps(completedSteps.filter(id => id !== stepId));
    } else {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const isCompleted = (stepId) => completedSteps.includes(stepId);

  const beforeYouStart = [
    {
      id: 'req-1',
      title: 'Valid Email Address',
      description: 'You will need a valid email address that you have access to. This will be used for account verification and communications.',
      icon: '📧',
      important: true
    },
    {
      id: 'req-2',
      title: 'Personal Information',
      description: 'Have your passport or travel document ready. You\'ll need details like passport number, date of birth, and nationality.',
      icon: ' ',
      important: true
    },
    {
      id: 'req-3',
      title: 'Strong Password',
      description: 'Prepare a strong password that meets the requirements: at least 8 characters, including uppercase, lowercase, numbers, and special characters.',
      icon: '🔐',
      important: true
    },
    {
      id: 'req-4',
      title: 'Security Questions',
      description: 'Think of answers to security questions that you\'ll remember. These are used for account recovery.',
      icon: '🔒',
      important: false
    },
    {
      id: 'req-5',
      title: 'Contact Details',
      description: 'Have your current residential address and phone number ready.',
      icon: '📞',
      important: false
    }
  ];

  const creationSteps = [
    {
      id: 'step-1',
      title: 'Access the ImmiAccount Website',
      icon: '🌐',
      details: [
        'Open your web browser (Chrome, Firefox, Safari, or Edge recommended)',
        'Go to the official ImmiAccount website: https://online.immi.gov.au/lusc/login',
        'Verify you\'re on the official Australian Government website (look for ".gov.au" in the URL)',
        'Ensure your internet connection is stable'
      ],
      tips: [
        'Bookmark the official website for future access',
        'Avoid clicking on links from emails - always type the URL directly',
        'Use a secure, private internet connection (avoid public WiFi)'
      ],
      warning: 'Never enter your details on unofficial websites or through email links to prevent phishing.'
    },
    {
      id: 'step-2',
      title: 'Click "Create ImmiAccount"',
      icon: '➕',
      details: [
        'On the login page, locate the "Create ImmiAccount" button',
        'Click on "Create ImmiAccount" to begin the registration process',
        'You will be redirected to the terms and conditions page',
        'The page will load with the heading "Create an ImmiAccount"'
      ],
      tips: [
        'If the button doesn\'t respond, try refreshing the page',
        'Make sure JavaScript is enabled in your browser',
        'Clear your browser cache if you encounter issues'
      ],
      warning: null
    },
    {
      id: 'step-3',
      title: 'Read and Accept Terms and Conditions',
      icon: '📜',
      details: [
        'Carefully read through the Terms and Conditions',
        'Review the Privacy Policy',
        'Understand your rights and responsibilities',
        'Check the box to indicate you accept the terms',
        'Click "Next" or "Continue" to proceed'
      ],
      tips: [
        'Take time to understand what you\'re agreeing to',
        'Pay attention to data collection and privacy policies',
        'Save or print a copy for your records if needed'
      ],
      warning: 'You must be 18 years or older to create an ImmiAccount. If under 18, a parent or guardian must create the account.'
    },
    {
      id: 'step-4',
      title: 'Enter Your Personal Details',
      icon: '👤',
      details: [
        'Select your title (Mr, Ms, Mrs, Dr, etc.)',
        'Enter your family name (surname) exactly as it appears on your passport',
        'Enter your given names (first and middle names)',
        'Select your gender/sex',
        'Enter your date of birth (DD/MM/YYYY format)',
        'Select your country of birth',
        'Enter your city/town of birth',
        'Double-check all information for accuracy'
      ],
      tips: [
        'Use CAPITAL LETTERS as they appear on your passport',
        'Include all middle names if they appear on your passport',
        'Be consistent with the spelling across all applications',
        'If you have one name only, enter it in both fields'
      ],
      warning: 'Information must match your passport exactly. Mismatched information can cause delays or rejections.'
    },
    {
      id: 'step-5',
      title: 'Enter Passport/Travel Document Details',
      icon: '🛂',
      details: [
        'Select document type (usually "Passport")',
        'Select the country that issued your passport',
        'Enter your passport number (no spaces or dashes)',
        'Select your nationality/citizenship',
        'Enter the passport issue date',
        'Enter the passport expiry date',
        'Some fields may auto-populate based on previous entries'
      ],
      tips: [
        'Remove any spaces or special characters from passport number',
        'Ensure your passport is valid for at least 6 months',
        'Have your physical passport in front of you',
        'Check the passport number carefully - common errors include mixing O/0 and I/1'
      ],
      warning: 'Your passport must be valid. An expired passport number will not be accepted.'
    },
    {
      id: 'step-6',
      title: 'Provide Contact Information',
      icon: '📬',
      details: [
        'Enter your current residential address',
        'Include unit/apartment number if applicable',
        'Enter street name and number',
        'Enter suburb/city',
        'Select state/province',
        'Enter postal/zip code',
        'Select country of residence',
        'Enter your phone number with country code',
        'Provide a secondary contact number (optional but recommended)'
      ],
      tips: [
        'Use your actual residential address, not a PO Box',
        'Format: +61 for Australia, +977 for Nepal, etc.',
        'Include area code for phone numbers',
        'Ensure you can receive mail at this address'
      ],
      warning: 'All official correspondence will be sent to this address. Keep it updated.'
    },
    {
      id: 'step-7',
      title: 'Create Your Email and Username',
      icon: '📧',
      details: [
        'Enter a valid email address (this will be your username)',
        'Re-enter the email address to confirm',
        'Ensure you have access to this email account',
        'The email must be unique and not already registered',
        'Check your email format (example@domain.com)',
        'Click "Check availability" if prompted'
      ],
      tips: [
        'Use a personal email you check regularly',
        'Avoid using temporary email services',
        'Gmail, Outlook, Yahoo are all acceptable',
        'Write down your email/username in a safe place',
        'Consider using an email you\'ll have long-term access to'
      ],
      warning: 'This email will be your login username. You cannot change it later, so choose carefully.'
    },
    {
      id: 'step-8',
      title: 'Create a Strong Password',
      icon: '🔑',
      details: [
        'Create a password meeting these requirements:',
        '• Minimum 8 characters long',
        '• At least one uppercase letter (A-Z)',
        '• At least one lowercase letter (a-z)',
        '• At least one number (0-9)',
        '• At least one special character (!@#$%^&*)',
        'Enter the password in the first field',
        'Re-enter the same password to confirm',
        'A password strength meter may appear'
      ],
      tips: [
        'Use a password manager to generate and store strong passwords',
        'Avoid using personal information (birthdays, names)',
        'Don\'t use the same password as other accounts',
        'Consider using a passphrase with numbers and symbols',
        'Example: MyTrip2Australia@2024!'
      ],
      warning: 'Never share your password. Department of Home Affairs will never ask for it via email or phone.'
    },
    {
      id: 'step-9',
      title: 'Set Up Security Questions',
      icon: '❓',
      details: [
        'Select your first security question from the dropdown',
        'Enter an answer you will remember',
        'Select your second security question',
        'Enter the answer for the second question',
        'Select your third security question',
        'Enter the answer for the third question',
        'All answers are case-sensitive',
        'Write down your questions and answers in a secure place'
      ],
      tips: [
        'Choose questions with answers that won\'t change over time',
        'Don\'t choose questions with answers others might know',
        'Use memorable but unique answers',
        'Consider using coded answers for extra security',
        'Store answers securely offline'
      ],
      warning: 'Security questions are used for account recovery. If you forget the answers, you may lose access to your account.'
    },
    {
      id: 'step-10',
      title: 'Review and Submit Your Information',
      icon: ' ',
      details: [
        'Review all information you\'ve entered carefully',
        'Check spelling of your name matches your passport',
        'Verify passport number is correct',
        'Confirm email address is accurate',
        'Ensure contact details are current',
        'Check the declaration checkbox',
        'Read the declaration statement',
        'Click "Submit" or "Create Account" button'
      ],
      tips: [
        'Take a screenshot of the summary page for your records',
        'Print or save the confirmation page',
        'Don\'t close the browser until you see confirmation',
        'Note down any reference numbers provided'
      ],
      warning: 'Once submitted, some information cannot be changed without contacting support.'
    },
    {
      id: 'step-11',
      title: 'Verify Your Email Address',
      icon: '✉️',
      details: [
        'Check your email inbox for a verification email',
        'The email will be from "noreply@homeaffairs.gov.au"',
        'Open the email and locate the verification link',
        'Click on the verification/activation link',
        'You will be redirected to the ImmiAccount website',
        'A confirmation message will appear',
        'Your account is now active and ready to use'
      ],
      tips: [
        'Check spam/junk folder if you don\'t see the email',
        'Verification link typically expires in 24-48 hours',
        'If expired, you can request a new verification email',
        'Add the sender to your contacts to prevent future emails from going to spam',
        'Keep this confirmation email for your records'
      ],
      warning: 'You must verify your email before you can access your ImmiAccount or submit applications.'
    },
    {
      id: 'step-12',
      title: 'Complete Your Account Setup',
      icon: '🎉',
      details: [
        'Log in to your new ImmiAccount using your email and password',
        'You\'ll be taken to your account dashboard',
        'Familiarize yourself with the interface',
        'Check that your details are correctly displayed',
        'Update any additional information if required',
        'Set up two-factor authentication if available (recommended)',
        'Add any family members or dependents if needed'
      ],
      tips: [
        'Take a tour of the dashboard features',
        'Bookmark the login page',
        'Enable notifications for application updates',
        'Keep your contact details updated',
        'Regularly check for messages from Home Affairs'
      ],
      warning: null
    }
  ];

  const afterCreationSteps = [
    {
      id: 'after-1',
      title: 'Secure Your Account',
      description: 'Keep your password safe and never share it. Enable two-factor authentication if available.',
      icon: '🔐'
    },
    {
      id: 'after-2',
      title: 'Update Contact Details',
      description: 'If your address, phone, or email changes, update it immediately in your ImmiAccount.',
      icon: '📝'
    },
    {
      id: 'after-3',
      title: 'Check Regularly',
      description: 'Log in regularly to check for messages, updates, or requests from the Department of Home Affairs.',
      icon: '🔔'
    },
    {
      id: 'after-4',
      title: 'Link Existing Applications',
      description: 'If you have any existing applications, you can link them to your ImmiAccount using the transaction reference number.',
      icon: '🔗'
    },
    {
      id: 'after-5',
      title: 'Start Your Visa Application',
      description: 'You can now begin your visa application process through your ImmiAccount.',
      icon: '✈️'
    }
  ];

  const troubleshooting = [
    {
      id: 'trouble-1',
      issue: 'Email verification not received',
      solutions: [
        'Check your spam/junk folder',
        'Wait 10-15 minutes as emails can be delayed',
        'Check that you entered your email correctly',
        'Request a new verification email from the login page',
        'Contact ImmiAccount support if still not received after 24 hours'
      ]
    },
    {
      id: 'trouble-2',
      issue: 'Passport number not accepted',
      solutions: [
        'Remove all spaces and special characters',
        'Check for typing errors (common: O vs 0, I vs 1)',
        'Ensure passport is not expired',
        'Verify country of issue matches your passport',
        'Try entering in all capital letters'
      ]
    },
    {
      id: 'trouble-3',
      issue: 'Password doesn\'t meet requirements',
      solutions: [
        'Ensure minimum 8 characters',
        'Include at least one uppercase letter',
        'Include at least one number',
        'Include at least one special character (!@#$%)',
        'Avoid spaces at beginning or end',
        'Try a password generator tool'
      ]
    },
    {
      id: 'trouble-4',
      issue: 'Email already registered',
      solutions: [
        'You may already have an ImmiAccount - try password recovery',
        'Use the "Forgot Password" option',
        'Contact support if you believe this is an error',
        'Try using a different email address',
        'Check if a family member created an account with this email'
      ]
    },
    {
      id: 'trouble-5',
      issue: 'Cannot access account after creation',
      solutions: [
        'Ensure you\'ve verified your email',
        'Check if your password is correct (case-sensitive)',
        'Clear browser cache and cookies',
        'Try a different browser',
        'Use password recovery if forgotten',
        'Contact ImmiAccount support'
      ]
    }
  ];

  const getTotalSteps = () => {
    return creationSteps.length;
  };

  const getCompletedCount = () => {
    return completedSteps.length;
  };

  const getProgress = () => {
    return Math.round((getCompletedCount() / getTotalSteps()) * 100);
  };

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh' }}>
      {/* Banner Section */}
      <div style={{
        background: 'linear-gradient(135deg, #7D178F 0%, #a042b8 100%)',
        padding: '60px 0',
        marginBottom: '40px'
      }}>
        <Container>
          <Row>
            <Col>
              <div className="text-center text-white">
                <h1 className="display-4 fw-bold mb-2">  ImmiAccount Creation Guide</h1>
                <p className="lead">Complete step-by-step instructions to create your Australian ImmiAccount</p>
                <div className="d-flex justify-content-center gap-2 mt-3 flex-wrap">
                  <Badge color="light" className="px-3 py-2" style={{ color: '#7D178F' }}>Official Guide</Badge>
                  <Badge color="light" className="px-3 py-2" style={{ color: '#7D178F' }}>Easy to Follow</Badge>
                  <Badge color="light" className="px-3 py-2" style={{ color: '#7D178F' }}>Detailed Steps</Badge>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="pb-5">
        {/* Navigation Tabs */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex gap-2 flex-wrap justify-content-center" >
              <Button
                style={{
                  backgroundColor: activeSection === 'before' ? '#7D178F' : 'transparent',
                  color: activeSection === 'before' ? '#ffffff' : '#7D178F',
                  border: `2px solid ${activeSection === 'before' ? '#7D178F' : '#dee2e6'}`
                }}
                onClick={() => setActiveSection('before')}
              >
                  Before You Start
              </Button>
              <Button
                style={{
                  backgroundColor: activeSection === 'steps' ? '#7D178F' : 'transparent',
                  color: activeSection === 'steps' ? '#ffffff' : '#7D178F',
                  border: `2px solid ${activeSection === 'steps' ? '#7D178F' : '#dee2e6'}`
                }}
                onClick={() => setActiveSection('steps')}
              >
                 Creation Steps
              </Button>
              <Button
                style={{
                  backgroundColor: activeSection === 'after' ? '#7D178F' : 'transparent',
                  color: activeSection === 'after' ? '#ffffff' : '#7D178F',
                  border: `2px solid ${activeSection === 'after' ? '#7D178F' : '#dee2e6'}`
                }}
                onClick={() => setActiveSection('after')}
              >
                  After Creation
              </Button>
              <Button
                style={{
                  backgroundColor: activeSection === 'troubleshoot' ? '#7D178F' : 'transparent',
                  color: activeSection === 'troubleshoot' ? '#ffffff' : '#7D178F',
                  border: `2px solid ${activeSection === 'troubleshoot' ? '#7D178F' : '#dee2e6'}`
                }}
                onClick={() => setActiveSection('troubleshoot')}
              >
                  Troubleshooting
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <Col
            lg={activeSection === "steps" ? 8 : 12}
            className="mx-auto"
          >

            {/* Before You Start Section */}
            {activeSection === 'before' && (
              <div >
                <Card className="shadow-lg border-0 rounded-3 mb-4 ">
                  <CardHeader className="bg-white border-0 pt-4">
                    <h4 className="mb-0 " style={{ color: '#7D178F' }}>
                        Before You Start
                    </h4>
                    <p className="text-muted mb-0 mt-2">Make sure you have these ready before creating your ImmiAccount</p>
                  </CardHeader>
                  <CardBody className="p-4">
                    {beforeYouStart.map((req, index) => (
                      <Card key={req.id} className="mb-3 border-0 bg-light">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-start">
                            <div style={{ fontSize: '2rem', marginRight: '15px' }}>
                              {req.icon}
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <h6 className="fw-bold mb-2">
                                  {req.title}
                                  {req.important && (
                                    <Badge color="danger" className="ms-2">Required</Badge>
                                  )}
                                </h6>
                              </div>
                              <p className="mb-0 text-muted">{req.description}</p>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))}

                    <Alert color="info" className="mt-4 border-0">
                      <div className="d-flex align-items-start">
                        <span className="me-2" style={{ fontSize: '1.5rem' }}>💡</span>
                        <div>
                          <strong>Pro Tip:</strong> Have all these items ready before you start. The process takes about 10-15 minutes if you have everything prepared.
                        </div>
                      </div>
                    </Alert>
                  </CardBody>
                </Card>
              </div>
            )}

            {/* Creation Steps Section */}
            {activeSection === 'steps' && (
              <div>
                {creationSteps.map((step, index) => (
                  <Card key={step.id} className="shadow-sm border-0 rounded-3 mb-3">
                    <CardHeader
                      className="bg-white border-0 d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleStep(step.id)}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: isCompleted(step.id) ? '#28a745' : '#7D178F',
                            color: '#ffffff',
                            fontSize: '1.2rem'
                          }}
                        >
                          {isCompleted(step.id) ? '✓' : step.icon}
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">
                            Step {index + 1}: {step.title}
                          </h6>
                          <small className="text-muted">
                            Click to {openStep === step.id ? 'collapse' : 'expand'} details
                          </small>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          size="sm"
                          style={{
                            backgroundColor: isCompleted(step.id) ? '#28a745' : '#e9ecef',
                            border: 'none',
                            color: isCompleted(step.id) ? '#ffffff' : '#6c757d'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            markComplete(step.id);
                          }}
                        >
                          {isCompleted(step.id) ? '✓ Done' : 'Mark Done'}
                        </Button>
                        <span style={{ fontSize: '1.2rem' }}>
                          {openStep === step.id ? '▲' : '▼'}
                        </span>
                      </div>
                    </CardHeader>
                    <Collapse isOpen={openStep === step.id}>
                      <CardBody className="p-4">
                        <div className="mb-4">
                          <h6 className="fw-bold mb-3" style={{ color: '#7D178F' }}>
                             Detailed Instructions:
                          </h6>
                          <ul className="list-unstyled">
                            {step.details.map((detail, idx) => (
                              <li key={idx} className="mb-2 d-flex">
                                <span className="me-2" style={{ color: '#7D178F' }}>▪</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {step.tips && step.tips.length > 0 && (
                          <Alert color="success" className="border-0 mb-3">
                            <h6 className="fw-bold mb-2">💡 Helpful Tips:</h6>
                            <ul className="mb-0 ps-3">
                              {step.tips.map((tip, idx) => (
                                <li key={idx} className="mb-1">{tip}</li>
                              ))}
                            </ul>
                          </Alert>
                        )}

                        {step.warning && (
                          <Alert color="warning" className="border-0 mb-0">
                            <div className="d-flex align-items-start">
                              <span className="me-2" style={{ fontSize: '1.3rem' }}>⚠️</span>
                              <div>
                                <strong>Important:</strong> {step.warning}
                              </div>
                            </div>
                          </Alert>
                        )}
                      </CardBody>
                    </Collapse>
                  </Card>
                ))}
              </div>
            )}

            {/* After Creation Section */}
            {activeSection === 'after' && (
              <Card className="shadow-lg border-0 rounded-3">
                <CardHeader className="bg-white border-0 pt-4">
                  <h4 className="mb-0" style={{ color: '#7D178F' }}>
                      After Creating Your ImmiAccount
                  </h4>
                  <p className="text-muted mb-0 mt-2">Important steps to take after your account is set up</p>
                </CardHeader>
                <CardBody className="p-4">
                  {afterCreationSteps.map((step, index) => (
                    <Card key={step.id} className="mb-3 border-0" style={{ backgroundColor: '#f8f9fa' }}>
                      <CardBody className="p-3">
                        <div className="d-flex align-items-start">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                              width: '50px',
                              height: '50px',
                              backgroundColor: '#7D178F',
                              color: '#ffffff',
                              fontSize: '1.5rem',
                              flexShrink: 0
                            }}
                          >
                            {step.icon}
                          </div>
                          <div>
                            <h6 className="fw-bold mb-2">{step.title}</h6>
                            <p className="mb-0 text-muted">{step.description}</p>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}

                  <Alert color="info" className="mt-4 border-0">
                    <div className="d-flex align-items-start">
                      <span className="me-2" style={{ fontSize: '1.5rem' }}>🎉</span>
                      <div>
                        <strong>Congratulations!</strong> Your ImmiAccount is now ready. You can use it to apply for visas, check application status, and communicate with the Department of Home Affairs.
                      </div>
                    </div>
                  </Alert>
                </CardBody>
              </Card>
            )}

            {/* Troubleshooting Section */}
            {activeSection === 'troubleshoot' && (
              <Card className="shadow-lg border-0 rounded-3">
                <CardHeader className="bg-white border-0 pt-4">
                  <h4 className="mb-0" style={{ color: '#7D178F' }}>
                      Common Issues & Solutions
                  </h4>
                  <p className="text-muted mb-0 mt-2">Facing problems? Here are solutions to common issues</p>
                </CardHeader>
                <CardBody className="p-4">
                  {troubleshooting.map((item, index) => (
                    <Card key={item.id} className="mb-3 border-0 bg-light">
                      <CardBody className="p-3">
                        <h6 className="fw-bold mb-3" style={{ color: '#7D178F' }}>
                            Problem: {item.issue}
                        </h6>
                        <div>
                          <strong className="text-success">  Solutions:</strong>
                          <ul className="mt-2 mb-0">
                            {item.solutions.map((solution, idx) => (
                              <li key={idx} className="mb-1">{solution}</li>
                            ))}
                          </ul>
                        </div>
                      </CardBody>
                    </Card>
                  ))}

                  <Alert color="warning" className="mt-4 border-0">
                    <div className="d-flex align-items-start">
                      <span className="me-2" style={{ fontSize: '1.5rem' }}>📞</span>
                      <div>
                        <strong>Still Need Help?</strong><br />
                        Contact the Department of Home Affairs:<br />
                        • Within Australia: 131 881<br />
                        • Outside Australia: +61 2 6196 0196<br />
                        • Email: <a href="mailto:support@homeaffairs.gov.au">support@homeaffairs.gov.au</a>
                      </div>
                    </div>
                  </Alert>
                </CardBody>
              </Card>
            )}
          </Col>

          {/* Sticky Progress Panel */}
          <Col lg={4}>
            <div style={{ position: 'sticky', top: '20px' }}>
              {activeSection === 'steps' && (
                <Card className="shadow-lg border-0 rounded-3 mb-3">
                  <div
                    className="text-white text-center py-4"
                    style={{
                      background: 'linear-gradient(135deg, #7D178F 0%, #a042b8 100%)'
                    }}
                  >
                    <div className="display-1 fw-bold mb-2">
                      {getCompletedCount()}/{getTotalSteps()}
                    </div>
                    <div className="h5 mb-0">Steps Complete</div>
                  </div>

                  <CardBody className="p-4">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <small className="text-muted fw-semibold">Progress</small>
                        <small className="fw-bold">{getProgress()}%</small>
                      </div>
                      <div className="progress mb-3">
                        <div
                          className="progress-bar bg-primary"
                          role="progressbar"
                          style={{ width: `${getProgress()}%` }}
                          aria-valuenow={getProgress()}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>

              )}
            </div>
          </Col>

        </Row>
      </Container>
    </div>
  )
}